import { ClbButton, ClbTooltip } from '@celebration/react';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useRef, useState } from 'react';
import { getLoadTagStatusType, isPastLoad } from '../../utils/loads-utils';
import LoadManager from '../../../../../common/services/loadmanager';
import { useToast } from '../../../../../app/contexts/toast/toast.context';
import AuthContext from '../../../../../app/contexts/auth/auth.context';
import LoadCardSuspensionModal from './components/load-card-suspension-modal';
import LoadCardReOfferModal from './components/load-card-reoffer-modal';
import ILoadCardProps from './interfaces/ILoadCardProps';
import {
    getCardCursor,
    suspensionMessage,
    noRatesMessage,
} from '../../../../../common/components/load-card/load-card-body/utils/load-card-body-utils';

import LoadHistoryDrawer from '../load-history-drawer';
import './load-card.scss';
import LoadCardFooter from '../../../../../common/components/load-card/load-card-footer/load-card-footer';
import LoadCardBody from '../../../../../common/components/load-card/load-card-body/load-card-body';
import {
    IRouteDetails,
    IWinnerDetails,
} from '../../../../../common/components/load-card/load-card-body/ILoadCardBody';
import LoadCardHeader from '../../../../../common/components/load-card/load-card-header/load-card-header';
import LoadStatus from '../../../../../common/enums/loads/load-status.enum';
import { loadIsConfirmed } from './utils/load-card-utils';
import ILoadsFiltersModel from '../../../../../common/interfaces/IFiltersModel';
import ILoadSuspensionRequest from '../../../../../common/interfaces/loads/ILoadSuspensionRequest';
import LoadCardDetail from '../../../../../common/components/load-card-detail';
import { clickCard } from '../../../../../common/utils/load-card-utils';

export default function LoadCard(props: Readonly<ILoadCardProps>) {
    const {
        loadInfo,
        numberOfSelectedCards,
        handleCardSelected,
        handleCardUnselected,
        reloadMainPage,
        updateItem,
    } = props;

    const { setToast } = useToast();
    const { user } = AuthContext.useAuth();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const hasRates = useMemo(() => loadInfo.rates.length > 0, [loadInfo]);

    const [
        isShowingSuspendLoadConfirmationModal,
        setIsShowingSuspendLoadConfirmationModal,
    ] = useState<boolean>();

    const [openHistoryDrawer, setOpenHistoryDrawer] = useState<boolean>(false);

    const [reason, setReason] = useState<string>('');
    const [openModalReOfferLoad, setOpenModalReOfferLoad] =
        useState<boolean>(false);
    const checkBoxRef = useRef<HTMLInputElement>(null);

    const isTmsConfirmed: boolean =
        loadInfo.tagStatus === LoadStatus.ConfirmedTms;

    const isLoadConfirmed: boolean = loadIsConfirmed(loadInfo, isTmsConfirmed);

    const isTmsCancelled: boolean =
        loadInfo.tagStatus === LoadStatus.CancelledTms;

    const isLoadSuspended: boolean =
        loadInfo.tagStatus === LoadStatus.Suspended;

    const isLoadCancelled: boolean =
        loadInfo.tagStatus === LoadStatus.Cancelled;

    const isLoadExpired: boolean = loadInfo.tagStatus === LoadStatus.Expired;

    const isLoadPlanned: boolean = loadInfo.tagStatus === LoadStatus.Planned;

    const isInvalidLoad = isPastLoad(loadInfo.routeDetails?.collectionDate);

    const suspensionReason = loadInfo.statusReason;

    const offerModel = useMemo(() => {
        if (user?.type !== 'ambev') {
            return null;
        }

        if (loadInfo.isPocFreightAuction) {
            return 'POC';
        }

        if (loadInfo.isPool !== undefined) {
            return loadInfo.isPool ? 'POOL' : 'ROUND';
        }

        return null;
    }, [user?.type, loadInfo.isPool, loadInfo.isPocFreightAuction]);

    const shouldDisablePrioritizedBtn = (): boolean => {
        return (
            isLoadSuspended ||
            isLoadCancelled ||
            isLoadConfirmed ||
            isLoadExpired ||
            isTmsCancelled ||
            isTmsConfirmed ||
            loadInfo.prioritized ||
            isInvalidLoad
        );
    };

    const shouldDisableSuspensionBtn = (): boolean => {
        return (
            isLoadSuspended ||
            isTmsCancelled ||
            isLoadCancelled ||
            isLoadExpired ||
            isTmsConfirmed ||
            isInvalidLoad
        );
    };

    const shouldShowReasonInfo = () => {
        return (
            (isLoadSuspended ||
                isTmsCancelled ||
                (!hasRates && !isLoadConfirmed) ||
                isLoadCancelled) &&
            suspensionReason !== undefined
        );
    };

    const shouldShowWinnerInfo = () => {
        return isLoadConfirmed && !loadInfo.cardMessage;
    };

    const showReOfferLoadConfirmationModal = (): void => {
        if (isLoadSuspended || isTmsCancelled) {
            setOpenModalReOfferLoad(true);
        }
    };

    const handleReOfferModalOnClose = (): void => {
        setOpenModalReOfferLoad(false);
    };

    const shouldDisableReOfferBtn = (): boolean => {
        return !(isLoadSuspended || isTmsCancelled || isInvalidLoad);
    };

    const showSuspendLoadConfirmationModal = useCallback(() => {
        setIsShowingSuspendLoadConfirmationModal(true);
    }, []);

    const closeSuspendLoadConfirmationModal = useCallback(() => {
        setIsShowingSuspendLoadConfirmationModal(false);
        setReason('');
    }, []);

    const getLoadInfo = useCallback(async () => {
        const filters = {
            loadId: [loadInfo.idLoad.toString()],
        } as ILoadsFiltersModel;

        LoadManager.getLoads(filters)
            .then(({ response, error }) => {
                if (error !== undefined) {
                    setToast('negative', t('Erro'), error.message);
                }

                if (Array.isArray(response!.data) && response!.data[0]) {
                    updateItem(response!.data[0]);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [setToast, t, loadInfo, updateItem]);

    const refreshLoad = useCallback(() => {
        setIsLoading(true);
        getLoadInfo();
    }, [getLoadInfo]);

    const prioritizeLoad = async () => {
        await LoadManager.prioritizeLoads([loadInfo.idLoad]).then(
            ({ response: data, error }) => {
                if (error !== undefined) {
                    setToast('negative', t('Erro'), error.message);
                    refreshLoad();
                    return;
                }

                if (data?.loadsIdWithError?.length) {
                    setToast(
                        'warning',
                        t('Atencao'),
                        `${t('LoadPrioritizeWithError')} ${data.loadsIdWithError}`,
                    );
                } else {
                    setToast('positive', t('Sucesso'), t('LoadPriorizada'));
                }
                reloadMainPage();
            },
        );
    };

    const handleOnReOffer = useCallback(() => {
        LoadManager.reOfferLoads([loadInfo.idLoad]).then(
            ({ response: data, error }) => {
                if (error !== undefined) {
                    setToast('negative', t('Erro'), error.message);
                }

                if (data !== undefined) {
                    if (data?.loadsIdWithError?.length) {
                        setToast(
                            'warning',
                            t('Atencao'),
                            `${t('LoadWithError')} ${data.loadsIdWithError}`,
                        );
                    } else {
                        setToast(
                            'positive',
                            t('Sucesso'),
                            t('LoadsReofertadas'),
                        );
                    }
                }

                refreshLoad();
            },
        );

        setOpenModalReOfferLoad(false);
    }, [t, setToast, refreshLoad, loadInfo.idLoad]);

    const submitSuspendLoad = useCallback(async () => {
        setIsShowingSuspendLoadConfirmationModal(false);

        const request = [
            {
                loadId: loadInfo.idLoad,
                reason,
            } as ILoadSuspensionRequest,
        ];

        const { response, error } = await LoadManager.suspendLoads(request);
        if (error !== undefined) {
            setToast('negative', t('Erro'), error.message);
        }

        if (response !== undefined) {
            setToast('positive', t('Sucesso'), t('LoadSuspensa'));
        }

        setReason('');
        refreshLoad();
    }, [refreshLoad, reason, setToast, t, loadInfo.idLoad]);

    const handleCardClick = useCallback(
        (event: any) => {
            clickCard(checkBoxRef.current!, event, numberOfSelectedCards);
        },
        [numberOfSelectedCards],
    );

    const getReasonMessage = () => {
        return !isLoadPlanned
            ? suspensionMessage(suspensionReason)
            : noRatesMessage(suspensionReason);
    };

    const renderLoadSuspensionConfirmationModal = () => {
        return (
            isShowingSuspendLoadConfirmationModal && (
                <LoadCardSuspensionModal
                    onSubmit={submitSuspendLoad}
                    onCancel={closeSuspendLoadConfirmationModal}
                    onChangeReason={setReason}
                />
            )
        );
    };

    const renderLoadReOfferConfirmationModal = () => {
        return (
            openModalReOfferLoad && (
                <LoadCardReOfferModal
                    reOfferModalOpen={openModalReOfferLoad}
                    reOfferModalOnClose={handleReOfferModalOnClose}
                    reOfferModalOnCancel={handleReOfferModalOnClose}
                    reOfferModalOnReOffer={handleOnReOffer}
                />
            )
        );
    };

    const renderLoadHistory = () => {
        return (
            openHistoryDrawer && (
                <LoadHistoryDrawer
                    loadData={loadInfo}
                    open={openHistoryDrawer}
                    setOpenHistoryDrawer={setOpenHistoryDrawer}
                    onUnsuspended={refreshLoad}
                />
            )
        );
    };

    const renderLoadCardDetails = () => {
        return (
            isLoadCardDetailVisible && (
                <div className="card-load-detail">
                    <LoadCardDetail
                        initialVisibility
                        onClose={handleClose}
                        loadData={loadInfo}
                    />
                </div>
            )
        );
    };

    const renderFooterButtonIcons = () => {
        return (
            <>
                <ClbTooltip
                    description={t('Info')}
                    position="top"
                    showArrow
                    colorMode="dark"
                >
                    <ClbButton
                        icon="info"
                        size="md"
                        styleType="icon"
                        data-testid="card-loads-footer__icons-info"
                        onClick={handleInfoClick}
                    />
                </ClbTooltip>
                <ClbTooltip
                    description={t('Historico')}
                    position="top"
                    showArrow
                    colorMode="dark"
                >
                    <ClbButton
                        icon="anotacoes"
                        size="md"
                        styleType="icon"
                        data-testid="card-loads-footer__icons-history"
                        onClick={() => setOpenHistoryDrawer(true)}
                    />
                </ClbTooltip>
                <ClbTooltip
                    description={t('Recarregar')}
                    position="top"
                    showArrow
                    colorMode="dark"
                >
                    <ClbButton
                        icon="refresh"
                        size="md"
                        styleType="icon"
                        data-testid="card-loads-footer__icons-refresh"
                        onClick={refreshLoad}
                    />
                </ClbTooltip>
                {!isLoadConfirmed && (
                    <ClbTooltip
                        description={t('Priorizar')}
                        position="top"
                        showArrow
                        colorMode="dark"
                    >
                        <ClbButton
                            icon="star-1"
                            size="md"
                            styleType="icon"
                            disabled={shouldDisablePrioritizedBtn()}
                            onClick={() => prioritizeLoad()}
                            data-testid="card-loads-footer__icons-prioritize"
                        />
                    </ClbTooltip>
                )}
            </>
        );
    };

    const renderFooterButtons = () => {
        return (
            <>
                <ClbButton
                    size="sm"
                    styleType="secondary"
                    label={t('Suspender')}
                    data-testid="card-loads-footer__buttons-secondary"
                    disabled={shouldDisableSuspensionBtn()}
                    onClick={showSuspendLoadConfirmationModal}
                />
                {isLoadConfirmed ? (
                    renderFooterButtonIcons()
                ) : (
                    <ClbButton
                        size="sm"
                        styleType="primary"
                        label={t('Reofertar')}
                        disabled={shouldDisableReOfferBtn()}
                        data-testid="card-loads-footer__buttons-primary"
                        onClick={showReOfferLoadConfirmationModal}
                    />
                )}
            </>
        );
    };

    const [isLoadCardDetailVisible, setIsLoadCardDetailVisible] =
        useState(false);

    const handleInfoClick = () => {
        setIsLoadCardDetailVisible(true);
    };

    const handleClose = () => {
        setIsLoadCardDetailVisible(false);
    };

    const offerModelClass = offerModel
        ? `card-loads--${offerModel.toLowerCase()}`
        : '';

    return (
        <div
            role="none"
            key={`${loadInfo.idLoad}`}
            data-testid="card-loads__card"
            className={`card-loads ${loadInfo.checked ? 'card-load__selected' : ''} ${offerModelClass}`}
            onClick={handleCardClick}
            style={{ cursor: getCardCursor(numberOfSelectedCards) }}
        >
            {isLoading ? (
                <ClbLoading />
            ) : (
                <div className="card-loads-main">
                    <LoadCardHeader
                        loadId={loadInfo.idLoad}
                        showLoadId
                        checkBoxRef={checkBoxRef}
                        checked={loadInfo.checked}
                        tag={getLoadTagStatusType(loadInfo.tagStatus, hasRates)}
                        isPrioritized={loadInfo.prioritized}
                        handleCardSelected={handleCardSelected}
                        handleCardUnselected={handleCardUnselected}
                    />

                    <LoadCardBody
                        sourceRoute={
                            {
                                code: loadInfo.routeDetails.originCode,
                                description:
                                    loadInfo.routeDetails.originDescription,
                                city: loadInfo.routeDetails.source.city,
                                federativeUnit:
                                    loadInfo.routeDetails.source.federativeUnit,
                            } as IRouteDetails
                        }
                        destinationRoute={
                            {
                                code: loadInfo.routeDetails.destinationCode,
                                description:
                                    loadInfo.routeDetails
                                        .destinationDescription,
                                city: loadInfo.routeDetails.destination.city,
                                federativeUnit:
                                    loadInfo.routeDetails.destination
                                        .federativeUnit,
                            } as IRouteDetails
                        }
                        channel={loadInfo?.channel}
                        loadType={loadInfo.type}
                        collectionDate={loadInfo.routeDetails.collectionDate}
                        showReason={shouldShowReasonInfo()}
                        reasonMessage={getReasonMessage()}
                        winner={
                            {
                                show: shouldShowWinnerInfo(),
                                code: loadInfo.winnerInfo?.carrierCode,
                                description: loadInfo.winnerInfo?.carrierName,
                            } as IWinnerDetails
                        }
                        skids={loadInfo.shipment.skids}
                        isReturnable={loadInfo.isReturnable}
                    />

                    <LoadCardFooter
                        iconButtons={renderFooterButtonIcons()}
                        mainButtons={renderFooterButtons()}
                        hiddenIconButtons={isLoadConfirmed}
                    />
                </div>
            )}

            {renderLoadCardDetails()}
            {renderLoadSuspensionConfirmationModal()}
            {renderLoadReOfferConfirmationModal()}
            {renderLoadHistory()}
        </div>
    );
}
